import { useEffect, useState } from 'react';
import { adminApi, type TransitReport } from '@/lib/adminApi';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { AlertTriangle, FileText, FlaskConical, Printer, ShieldCheck, X } from 'lucide-react';
import TransitReportView, { reportPeriod } from './b2g/TransitReportView';

const PERIODS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '365', label: 'Last 12 months' },
];

/**
 * B2G dashboard: the public-transport report sold to ministries and the DDA.
 * Aggregated figures only; team test activity stays out unless switched on,
 * and a printout that includes it is marked as an internal preview.
 */
export default function B2GDashboard() {
  const [days, setDays] = useState('30');
  const [includeTeam, setIncludeTeam] = useState(false);
  const [report, setReport] = useState<TransitReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [printOpen, setPrintOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminApi
      .getB2GTransitReport(parseInt(days, 10), includeTeam)
      .then((r) => {
        if (!cancelled) setReport(r);
      })
      .catch((err: any) => {
        if (!cancelled) setError(err?.message || 'Could not load the transit report');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days, includeTeam]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">B2G Dashboard</h1>
          <p className="text-sm text-gray-400 mt-1">
            Public transport report for government partners
            {report ? ` · ${reportPeriod(report)}` : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer select-none">
            <Switch checked={includeTeam} onCheckedChange={setIncludeTeam} aria-label="Include team test activity" />
            Include team test activity
          </label>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-36 bg-gray-900 border-gray-800 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-gray-900 border-gray-800">
              {PERIODS.map((p) => (
                <SelectItem key={p.value} value={p.value} className="text-gray-300 focus:bg-gray-800 focus:text-white">
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            className="border-gray-700 text-gray-300 hover:text-white"
            onClick={() => setPrintOpen(true)}
            disabled={!report || loading}
          >
            <FileText className="w-4 h-4 mr-2" /> Export report
          </Button>
        </div>
      </div>

      {report && !includeTeam && report.team_trips_excluded > 0 && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-cyan-500/5 border border-cyan-500/20 text-sm">
          <ShieldCheck className="w-4 h-4 text-cyan-400 mt-0.5 shrink-0" />
          <p className="text-gray-300">
            {report.team_trips_excluded.toLocaleString()} team test trip{report.team_trips_excluded === 1 ? '' : 's'} in this period {report.team_trips_excluded === 1 ? 'is' : 'are'} not counted, so these figures are real riders only. Switch on team test activity to preview the report with data.
          </p>
        </div>
      )}
      {report && includeTeam && (
        <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-500/5 border border-amber-500/20 text-sm">
          <FlaskConical className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
          <p className="text-gray-300">
            Showing team test activity. An export from this view is marked internal preview and must not be shared outside the company.
          </p>
        </div>
      )}

      {loading && !report ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-24 bg-gray-800/50 rounded-xl" />
            ))}
          </div>
          <Skeleton className="h-64 bg-gray-800/50 rounded-xl" />
        </div>
      ) : error ? (
        <div className="p-6 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <p className="font-medium">Couldn't load the transit report</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        </div>
      ) : report ? (
        <div className={loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
          <TransitReportView report={report} />
        </div>
      ) : null}

      {printOpen && report && <PrintableReport report={report} onClose={() => setPrintOpen(false)} />}
    </div>
  );
}

/** White A4 version of the report, ready to print or save as PDF. */
function PrintableReport({ report, onClose }: { report: TransitReport; onClose: () => void }) {
  const generated = new Date(report.generated_at).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto print:bg-white print:p-0 print:relative print:overflow-visible"
      onClick={onClose}
    >
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #b2g-printable, #b2g-printable * { visibility: visible; }
          #b2g-printable {
            position: absolute;
            left: 0; top: 0; right: 0;
            margin: 0; padding: 0;
            box-shadow: none !important;
            background: white !important;
          }
          #b2g-printable .b2g-no-print { display: none !important; }
          @page { margin: 14mm; size: A4 portrait; }
        }
      `}</style>
      <div
        id="b2g-printable"
        className="bg-white text-gray-900 rounded-xl shadow-2xl max-w-[960px] w-full mt-8 mb-12 print:shadow-none print:rounded-none print:max-w-none print:m-0"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="b2g-no-print sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between z-10 rounded-t-xl">
          <div className="text-sm text-gray-500">Transit report · ready to print</div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={onClose} className="border-gray-300 text-gray-700">
              <X className="w-3.5 h-3.5 mr-1.5" /> Close
            </Button>
            <Button size="sm" onClick={() => window.print()} className="bg-emerald-600 hover:bg-emerald-500 text-white">
              <Printer className="w-3.5 h-3.5 mr-1.5" /> Print / Save PDF
            </Button>
          </div>
        </div>

        <div className="p-8 print:p-0 space-y-5">
          {report.include_team && (
            <div className="border-2 border-red-500 text-red-600 rounded-lg px-4 py-2 text-sm font-semibold text-center">
              INTERNAL PREVIEW · includes H-Plate team test activity · not for distribution
            </div>
          )}
          <header className="flex items-start justify-between border-b-2 border-gray-900 pb-5">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-6 h-6 rounded-md bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center shrink-0">
                  <span className="text-white font-bold text-xs">H</span>
                </div>
                <span className="text-sm tracking-widest uppercase text-gray-500 font-semibold">
                  H-Plate · Transit Report
                </span>
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mt-2">Dominica public transport</h1>
              <p className="text-sm text-gray-500 mt-1">{reportPeriod(report)}</p>
            </div>
            <div className="text-right text-xs text-gray-500">
              <p>Prepared by Nex Paradise Ltd</p>
              <p className="mt-0.5">Generated {generated}</p>
            </div>
          </header>
          <TransitReportView report={report} paper />
        </div>
      </div>
    </div>
  );
}
